import eventBus from "@sd-utility/eventbus";
import { SrOnly } from "@tvg/fuse-utils";
import { getRecentSearches } from "client/api/recent-searches-service";
import { handleGetSFL } from "client/api/save-for-later-service";
import { DataContext, DataContextProvider } from "client/context/DataContext";
import useGlobalContext from "client/context/GlobalContext";
import { useSRAutosuggest } from "client/hooks/useSRAutosuggest";
import {
  formatResponse,
  isValidASTerm,
  sanitizeInput,
} from "client/utils/helpers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ASResponse, SearchResponse } from "types/searchApi/response";
import { RECENT_SEARCHES_STORAGE_EVENT, SEARCH } from "../../utils/constants";
import CloseButton from "../close-button/close-button";
import SearchBar from "../search-bar";
import { SearchChangeEventHandler } from "../search-bar/search-input";
import SearchResults from "../search-results/search-results";
import CloseContainer from "./close-container";
import {
  HeaderContainer,
  MockLogo,
  SearchContainer,
  SearchLabel,
  StyledSearchLabelRowNonDesktop,
} from "./modal-content.styles";

const formatData = (
  initData: SearchResponse | ASResponse | null,
  searchTerm?: string,
) => {
  const recentSearches = getRecentSearches();
  const isRecommendations = !isValidASTerm(searchTerm);
  return formatResponse(
    initData,
    isRecommendations,
    recentSearches,
    searchTerm,
  );
};

const ModalContent = (): JSX.Element => {
  const { getResults, initData, brand } = useGlobalContext();

  const [data, setData] = useState<DataContext>(() => formatData(initData));

  const srMessage = useSRAutosuggest(data);
  useEffect(() => {
    if (brand !== "veryireland") {
      handleGetSFL();
    }
  }, []);

  useEffect(() => {
    if (!data) setData(formatData(initData));
  }, [initData, data]);

  const handleSearchChange = useCallback<SearchChangeEventHandler>(
    async (_e, searchTerm) => {
      const sanitizedTerm = sanitizeInput(searchTerm);
      const res = await getResults(sanitizedTerm);
      setData(formatData(res, sanitizedTerm));
    },
    [getResults],
  );

  useEffect(() => {
    const sub = eventBus.subscribe(RECENT_SEARCHES_STORAGE_EVENT, (e) => {
      if (e.oldValue === e.newValue) return;
      const newRecentSearches = JSON.parse(e.newValue);
      setData((v) => ({
        ...v,
        searches: { ...v.searches, recent: newRecentSearches },
      }));
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  const isThreeColumnProducts = useMemo(() => {
    const { searchedTerm, products, searches, brands } = data;
    if (!!searches.suggested?.length || !products?.length) {
      return false;
    }

    if (isValidASTerm(searchedTerm)) {
      return !brands?.length;
    }

    return !searches?.recent?.length;
  }, [data]);

  return (
    <HeaderContainer data-testid="modal-container">
      <StyledSearchLabelRowNonDesktop data-testid="search-label-non-desktop">
        <SearchLabel type="heading" size="3">
          {SEARCH}
        </SearchLabel>

        <CloseButton dataTestId="search-close-button-mobile" />
      </StyledSearchLabelRowNonDesktop>

      <SearchContainer>
        <MockLogo />
        <SrOnly role="alert" aria-atomic="true" aria-live="assertive">
          {srMessage}
        </SrOnly>
        <SearchBar brand={brand} onChange={handleSearchChange} />
        <CloseContainer />
      </SearchContainer>

      <DataContextProvider value={data}>
        <SearchResults
          data-testid="modal-search-results"
          isThreeColumn={isThreeColumnProducts}
          brand={brand}
        />
      </DataContextProvider>
    </HeaderContainer>
  );
};

export default ModalContent;
